import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { BoardEvent } from './types'
import { keys } from './queries'

/**
 * Subscribes to a project's live-sync stream.
 *
 * The stream carries identity, not content: an event says "task X in project Y
 * changed", and this hook responds by invalidating the matching query. Nothing
 * patches local state from the wire, so a dropped, duplicated or out-of-order
 * event can never leave the board showing something that is not true.
 *
 * EventSource reconnects on its own, and a reconnect refetches everything
 * anyway, which is why the server can afford to drop a slow subscriber.
 */
export function useProjectEvents(projectId: string | undefined, selfId: string | undefined) {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)

  // Task ids touched by somebody else, used to pulse the affected card once.
  const [recentlyTouched, setRecentlyTouched] = useState<Record<string, number>>({})
  const timers = useRef<number[]>([])

  useEffect(() => {
    if (!projectId) return

    const source = new EventSource(`/api/projects/${projectId}/events`)
    source.onopen = () => setConnected(true)
    source.onerror = () => setConnected(false)

    const handle = (raw: MessageEvent) => {
      let event: BoardEvent
      try {
        event = JSON.parse(raw.data)
      } catch {
        return
      }

      queryClient.invalidateQueries({ queryKey: keys.board(projectId) })
      queryClient.invalidateQueries({ queryKey: keys.tasks() })
      if (event.taskId) {
        queryClient.invalidateQueries({ queryKey: keys.task(event.taskId) })
      }
      if (event.type === 'statuses.changed' || event.type === 'project.updated') {
        queryClient.invalidateQueries({ queryKey: keys.statuses(projectId) })
        queryClient.invalidateQueries({ queryKey: keys.projects() })
      }

      // Your own changes already animated when you made them; only somebody
      // else's edits are worth drawing the eye to.
      if (event.taskId && event.actorId && event.actorId !== selfId) {
        const taskId = event.taskId
        setRecentlyTouched((prev) => ({ ...prev, [taskId]: Date.now() }))
        const timer = window.setTimeout(() => {
          setRecentlyTouched((prev) => {
            const next = { ...prev }
            delete next[taskId]
            return next
          })
        }, 1200)
        timers.current.push(timer)
      }
    }

    for (const type of [
      'task.created', 'task.updated', 'task.moved', 'task.deleted',
      'comment.created', 'statuses.changed', 'project.updated',
    ]) {
      source.addEventListener(type, handle as EventListener)
    }

    return () => {
      source.close()
      setConnected(false)
      for (const t of timers.current) window.clearTimeout(t)
      timers.current = []
    }
  }, [projectId, selfId, queryClient])

  return { connected, recentlyTouched }
}
